// The document list used on the customer card, a document's page (its
// related documents), the employee profile document page, an order's
// amendments and a project. One row component for all of them; what a row
// offers depends on the props the screen passes (see FilesProps).
// Rows: file-row.tsx (≥ md) or file-card.tsx (phone); rules: file-row-model.ts
// and file-row-menu-rules.ts.
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppSelector } from "hooks";
import AttachToDocumentModal from "components/attach-to-document/AttachToDocumentModal";
import MakeSeparateDocumentModal from "components/attach-to-document/MakeSeparateDocumentModal";
import FileCard from "./file-card";
import FileRow from "./file-row";
import type { FileItem } from "./file-item";
import { canOpenDocumentPage } from "./file-row-menu-rules";
import type { FilesListContext } from "./use-file-row";
import useIsPhone from "./use-is-phone";

export interface FilesProps {
  // Each screen sends its own payload shape (customer list, fetch-by-id,
  // order, project); the rows read every one of them as a FileItem.
  files: any[];
  // Turns on the customer-side signing actions ("Pasirašymo prašymas",
  // "Siųsti el. laišką", "Įkelti pasirašytą dokumentą").
  customerId?: string;
  // On a document's page: the parent document's customer(s), offered when
  // a related document is sent for signing.
  parentFileCustomerId?: string;
  parentFileCustomers?: { id: string; name?: string }[];
  // Template used to edit a row that has none of its own (order amendments).
  parentTemplateId?: string;
  isEditable?: boolean;
  subject?: string;
  // "document" on a document's page: related rows get the send / edit
  // signature request actions.
  source?: string;
  orderEntity?: { id: string } | null;
  isOrderAmendment?: boolean;
  // "Perkelti prie kito dokumento" / "Padaryti atskiru dokumentu" in the row menu.
  allowParentActions?: boolean;
  onDocumentMoved?: () => void;
  // A related document filed without its own category links to the
  // document page under the parent's category.
  parentCategoryId?: string;
  // Checkbox per top-level row (customer card → document cart).
  selectedFileIds?: Record<string, boolean>;
  onToggleFileSelection?: (item: FileItem) => void;
  // The screen's own refresh, run after a row action changed a document
  // (sign request, e-mail, signed copy upload, click-to-sign, edit).
  onChanged?: () => void;
}

const NO_CUSTOMERS: { id: string; name?: string }[] = [];
const NO_SELECTION: Record<string, boolean> = {};

export default function Files({
  files,
  customerId = "",
  parentFileCustomerId = "",
  parentFileCustomers = NO_CUSTOMERS,
  parentTemplateId = "",
  isEditable = true,
  subject = "",
  source = "",
  orderEntity = null,
  isOrderAmendment = false,
  allowParentActions = false,
  onDocumentMoved,
  parentCategoryId = "",
  selectedFileIds = NO_SELECTION,
  onToggleFileSelection,
  onChanged,
}: FilesProps) {
  const { companyId, customerId: routeCustomerId } = useParams();
  const role = useAppSelector((state) => state.authReducer.role);
  const isPhone = useIsPhone();

  // One shared instance of each tree-move modal for the whole list.
  const [moveModalFile, setMoveModalFile] = useState<FileItem | null>(null);
  const [separateModalFile, setSeparateModalFile] = useState<FileItem | null>(null);
  const moveModalFileIds = useMemo(
    () => (moveModalFile ? [moveModalFile.id] : []),
    [moveModalFile]
  );
  // Names the file in AttachToDocumentModal's failure list.
  const moveModalFileLabels = useMemo(
    () =>
      moveModalFile
        ? { [moveModalFile.id]: moveModalFile.name || moveModalFile.title || "" }
        : {},
    [moveModalFile]
  );

  const ctx: FilesListContext = {
    companyId,
    routeCustomerId,
    currentUserId: role?.user,
    canOpenDocumentPage: canOpenDocumentPage(role?.permissionsgroups),
    customerId,
    parentFileCustomerId,
    parentFileCustomers: Array.isArray(parentFileCustomers) ? parentFileCustomers : NO_CUSTOMERS,
    parentTemplateId,
    parentCategoryId,
    isEditable,
    subject,
    source,
    orderEntity,
    isOrderAmendment,
    allowParentActions,
    selectedFileIds,
    onToggleFileSelection,
    onChanged,
    openMoveModal: setMoveModalFile,
    openMakeSeparateModal: setSeparateModalFile,
  };

  const Row = isPhone ? FileCard : FileRow;

  return (
    <>
      <div className={isPhone ? "flex flex-col gap-3" : "flex flex-col"}>
        {files.map((item) => (
          <Row key={item.id} item={item} ctx={ctx} />
        ))}
      </div>
      {companyId && (
        <>
          <AttachToDocumentModal
            companyId={companyId}
            fileIds={moveModalFileIds}
            fileLabels={moveModalFileLabels}
            open={!!moveModalFile}
            onClose={() => setMoveModalFile(null)}
            onDone={(result) => {
              if (result.ok.length > 0) {
                onDocumentMoved?.();
              }
            }}
          />
          <MakeSeparateDocumentModal
            companyId={companyId}
            fileId={separateModalFile?.id || ""}
            isSigned={!!separateModalFile?.isSigned}
            annulledAt={separateModalFile?.annulledAt ? String(separateModalFile.annulledAt) : null}
            open={!!separateModalFile}
            onClose={() => setSeparateModalFile(null)}
            onDone={() => onDocumentMoved?.()}
          />
        </>
      )}
    </>
  );
}
